/**
 * Naming rule for argument and variable keys (DEV-651).
 *
 * Karel's ask: *"Pridejme validator na zadani arg/vars — bez mezer. Jenom
 * podrzitka, pomlcky."* Today the only checks are emptiness and uniqueness, so
 * `verze videa` is accepted and ships into generated embed code as a key with a
 * space in it.
 *
 * ## Why lowercase, when the live data is SCREAMING_SNAKE
 *
 * Every existing key is upper case (`VERZE_VIDEA`, `GENDER`, `PHOTO`…) because
 * all seven entry points used to `.toUpperCase()` unconditionally. The decision
 * taken for this ticket is **lowercase for new keys, and nothing is renamed** —
 * an arg key is a join key replicated across `attributes` maps, `groups` arrays,
 * `dataVariable`, `effects`, `namesMap`, *and* embed snippets already pasted on
 * client sites and published `project.json` on S3. Those last two are outside
 * our reach, so a bulk rename would silently break live campaigns.
 *
 * The result is a mixed-case world, which creates exactly one hazard: opening a
 * project whose def is `GENDER`, typing `gender`, and getting a **second,
 * unrelated key**. That is prevented by making the duplicate check
 * case-insensitive — see {@link findExistingKey}. The user is told the existing
 * key is there and attaches it instead of creating a twin.
 *
 * Legacy keys are never rewritten, only flagged: {@link argKeyError} is applied
 * as a hard block to keys being **created**, and the same rule drives a
 * non-blocking badge on definitions that already exist.
 */

/** Letters, digits, underscores and hyphens — hyphens per Karel's message. */
const VALID_KEY = /^[a-z][a-z0-9_-]*$/;

/**
 * Reserved var key whose value lists the personalisation fields (DEV-573).
 * Mirrors `PERSONALIZATION_FIELDS_KEY` in
 * `backend/src/services/personalized-link.service.ts`.
 */
export const PERSONALIZATION_FIELDS_KEY = 'PERSONALIZATION_FIELDS';

// Readers match these exactly, so they keep their spelling however they are typed.
const RESERVED_KEYS = [PERSONALIZATION_FIELDS_KEY];

function reservedKey(raw: string): string | undefined {
	const upper = raw.trim().toUpperCase();
	return RESERVED_KEYS.find(key => key === upper);
}

/**
 * Normalise a key the user typed. Trim and lower-case, nothing else.
 *
 * A space is deliberately **not** rewritten into an underscore: silently
 * turning `verze videa` into `verze_videa` is how a second key appears next to
 * an existing one without anyone noticing. Spaces are an error the user has to
 * resolve — see {@link argKeyError}.
 */
export function normalizeArgKey(raw: string): string {
	return reservedKey(raw) ?? raw.trim().toLowerCase();
}

/**
 * The existing key this input refers to, compared case-insensitively, or
 * `undefined` if it is new.
 *
 * This is what keeps a lowercase `gender` from becoming a twin of a legacy
 * `GENDER`: callers report the collision instead of creating a second def.
 */
export function findExistingKey(raw: string, existingKeys: readonly string[]): string | undefined {
	const needle = raw.trim().toLowerCase();
	if (!needle) return undefined;
	return existingKeys.find(key => key.toLowerCase() === needle);
}

/**
 * Why this key cannot be used, or `null` when it is fine.
 *
 * `existingKeys` is optional: pass it when creating (so duplicates are caught,
 * including a differently-cased twin), omit it when only the shape matters —
 * such as badging a definition that already exists.
 */
export function argKeyError(raw: string, existingKeys?: readonly string[]): string | null {
	const trimmed = raw.trim();
	if (!trimmed) return 'Key is required';

	const normalized = normalizeArgKey(trimmed);
	if (!reservedKey(trimmed)) {
		if (/\s/.test(trimmed)) return 'No spaces — use an underscore or a hyphen';
		if (/^[0-9]/.test(normalized)) return 'Must start with a letter';
		if (!VALID_KEY.test(normalized)) return 'Only letters, digits, underscores and hyphens';
	}

	if (existingKeys) {
		const existing = findExistingKey(trimmed, existingKeys);
		if (existing) {
			// Name the key as it is actually stored: telling someone who typed
			// `gender` that "gender already exists" is baffling when the list in
			// front of them shows `GENDER`.
			return existing === normalized
				? 'Key already exists'
				: `Key already exists as "${existing}" — use that one`;
		}
	}
	return null;
}

/**
 * Whether an **existing** key departs from the convention. Drives the advisory
 * badge; never blocks anything, because renaming it would break already
 * distributed embed codes and published projects.
 *
 * Unlike {@link argKeyError} this does **not** normalise first. That function
 * judges what the user typed, which is about to be lower-cased anyway, so
 * `GENDER` is valid input. This one judges a key as it is *stored*, where case
 * is exactly what distinguishes a legacy `GENDER` from a conforming `gender`.
 */
export function isLegacyArgKey(key: string): boolean {
	return !RESERVED_KEYS.includes(key.trim()) && !VALID_KEY.test(key.trim());
}

/**
 * Whether a stored key is not merely old-style but genuinely **unusable**.
 *
 * The distinction matters because every key in every live project is
 * SCREAMING_SNAKE — flagging all of them would put a warning badge on 100% of
 * rows, which is wallpaper, not a signal. `GENDER` works perfectly; it is only
 * out of step with the new convention, and renaming it would break published
 * embed codes for no gain.
 *
 * What actually hurts is a key carrying a character that has no business in an
 * identifier — a space above all, which is the case in this ticket: it reaches
 * generated embed code and customer CSV headers verbatim. Those are rare and
 * worth pointing at.
 */
export function isMalformedArgKey(key: string): boolean {
	const trimmed = key.trim();
	if (!trimmed) return true;
	return /[^A-Za-z0-9_-]/.test(trimmed) || /^[0-9]/.test(trimmed);
}
